import { inject, injectionTarget } from '../../../loader/decorators/inject.js';
import { onPhase, Phase } from '../../../loader/decorators/on-phase.js';
import { ConfigResolver } from '../../../loader/essentials/config-resolver.js';
import { selectTargetingSettings } from '../../../loader/essentials/config-selector.js';
import { logger } from '../../../loader/essentials/logger.js';
import { ConsentCache } from '../../../loader/modules/consent-cache.js';
import { CONFIG_RESOLVER, CONSENT_CACHE } from '../../../loader/token.js';
import { sendCorsRequestPromise } from '../../../utils/send-cors-request.js';
import { GLOBAL_TARGETING, STORAGE_HELPER } from '../../token.js';
import { GlobalTargeting } from '../global-targeting/global-targeting.js';
import { StorageConsent, StorageHelper, StorageType } from '../storage/storage-helper.js';

const log = logger.logGroup({ prefix: 'CountryCodeTargeting' });

@injectionTarget()
export class CountryCodeTargeting {

    @inject(CONSENT_CACHE) private consentCache!: ConsentCache;
    @inject(CONFIG_RESOLVER) private configResolver!: ConfigResolver;
    @inject(GLOBAL_TARGETING) private globalTargeting!: GlobalTargeting;
    @inject(STORAGE_HELPER) private storageHelper!: StorageHelper;

    @onPhase({ phase: Phase.TARGETING })
    async getCountryCodeTargeting(): Promise<void> {
        // if its disabled in the config, we do not need the country targeting
        if (!this.configResolver.get(selectTargetingSettings())?.detectCountryCode) {
            log.debug('disabled in config');
            return;
        }

        // check if we are allowed to collect the country code
        const allowed = await this.consentCache.whenConsentConditions([
            { consentedPurposes: [2], consentedVendors: [1057] },
            { legitimateInterestPurposes: [2], legitimateInterestVendors: [1057] },
            { gdprApplies: false }
        ]);

        if (!allowed) return;

        // try to use caching to reduce costs; only reads from cache if consent is available
        const ccCached = await this.storageHelper.get(StorageType.SESSION, StorageConsent.IF_AVAILABLE_OR_DEFAULT, 'sdgCc');
        if (ccCached && /^[a-z]+$/.test(ccCached)) {
            log.debug('setting country code to %s (cached)', [ccCached]);
            this.globalTargeting.setValues('cc', [ccCached]);
            return;
        }

        // call endpoint and fetch country code
        const { cc } = await sendCorsRequestPromise<{ cc: string }>({
            src: 'https://cdn.stroeerdigitalgroup.de/service/cc.json',
        });

        log.debug('setting country code to %s', [cc]);
        this.globalTargeting.setValues('cc', [cc]);

        // cache country code for the next page impression; do NOT await the write operation which waits for consent
        this.storageHelper.set(StorageType.SESSION, StorageConsent.WAIT, 'sdgCc', cc);
    }
}
